import { faCheck, faMagic } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Col, DatePicker, Input, Modal, Row, Select, Space, Switch, Typography } from "antd"
import { Dayjs } from "dayjs"
import { useEffect, useMemo, useState } from "react"
import RichTextEditor from "../RichTextEditor/RichTextEditor"
import VSpace from "../common/VSpace"
import { Consult, CustomerDetail, EmailTemplate, SalesUser, SchedulableProjects } from "./customerApi"
import {
  allSchedulableProjects,
  buildConsultFormValues,
  canSaveConsult,
  ConsultAutoFillContext,
  ConsultFormValues,
  ConsultStatuses,
  isAnySalesLeadSelected,
  regenerateConsultAutoFill,
  toConsultSaveRequest,
} from "./customerConsultsModel"
import { useAddEditConsultationMutation } from "./customerQueries"

const { Text } = Typography

const statusOptions = [
  { value: ConsultStatuses.Scheduled, label: 'Scheduled' },
  { value: ConsultStatuses.Pending, label: 'Pending' },
  { value: ConsultStatuses.Sold, label: 'Sold' },
  { value: ConsultStatuses.Lost, label: 'Lost' },
  { value: ConsultStatuses.Canceled, label: 'Canceled' },
]

const DATE_FORMAT = 'M/D/YYYY h:mm A'

type Props = {
  open: boolean
  consult: Consult | null
  customer: CustomerDetail
  salesUsers: SalesUser[]
  salesLeads: string[]
  cannedResponses: EmailTemplate[]
  projects: SchedulableProjects | undefined
  onClose: () => void
  onSaved: (saved: Consult, send: boolean) => void
}

const ConsultationModal = ({ open, consult, customer, salesUsers, salesLeads, cannedResponses, projects, onClose, onSaved }: Props) => {
  const [values, setValues] = useState<ConsultFormValues>(() => buildConsultFormValues(consult, customer.id, customer.salesLead))
  const saveMutation = useAddEditConsultationMutation()

  useEffect(() => {
    if (open) {
      setValues(buildConsultFormValues(consult, customer.id, customer.salesLead))
    }
  }, [open, consult, customer.id, customer.salesLead])

  const projectsList = useMemo(() => allSchedulableProjects(projects), [projects])

  const autoFillContext: ConsultAutoFillContext = useMemo(() => ({
    customer: {
      displayName: customer.displayName,
      address1: customer.address1,
      city: customer.city,
      state: customer.state,
      zip: customer.zip,
      phones: customer.phones ?? [],
      emails: customer.emails ?? [],
    },
    salesUsers,
    projects: projectsList,
    cannedResponses,
  }), [customer, salesUsers, projectsList, cannedResponses])

  const update = (patch: Partial<ConsultFormValues>, regenFields: ('description' | 'emailBody' | 'summary')[] = []) => {
    setValues(prev => {
      const next = { ...prev, ...patch }
      return regenFields.length ? regenerateConsultAutoFill(next, autoFillContext, regenFields) : next
    })
  }

  const organizerOptions = salesUsers.map(u => ({ label: u.displayName, value: u.email }))

  const projectOptions = [
    ...(projects?.unscheduled ?? []).map(p => ({ label: p.name, value: p.id })),
    ...(projects?.scheduled?.length
      ? [{
          label: 'Projects already scheduled for consultation',
          options: projects.scheduled.map(p => ({ label: p.name, value: p.id })),
        }]
      : []),
  ]

  const cannedResponseOptions = [
    { label: 'Default template', value: '' },
    ...cannedResponses.map(cr => ({ label: cr.name, value: cr.id.toString() })),
  ]

  const salesLeadSelected = isAnySalesLeadSelected(values.selectedSalesLeads)
  const canSave = canSaveConsult(values)
  const isNew = values.id === 0

  const handleSave = (send: boolean) => {
    saveMutation.mutate(toConsultSaveRequest(values), {
      onSuccess: saved => {
        onSaved(saved, send)
        onClose()
      },
    })
  }

  return <Modal
    open={open}
    title="Schedule Consultation"
    width={900}
    onCancel={onClose}
    footer={[
      <Button key="cancel" onClick={onClose}>{isNew ? 'Cancel' : 'Close'}</Button>,
      <Button key="save" type="primary" icon={<FontAwesomeIcon icon={faCheck} />} disabled={!canSave} loading={saveMutation.isLoading} onClick={() => handleSave(false)}>
        {isNew ? 'Save' : 'Update'}
      </Button>,
      <Button key="save-send" type="primary" icon={<FontAwesomeIcon icon={faCheck} />} disabled={!canSave} loading={saveMutation.isLoading} onClick={() => handleSave(true)}>
        {isNew ? 'Save and Send' : 'Update and Send'}
      </Button>,
    ]}
  >
    <VSpace size="middle">
      <Row gutter={16}>
        <Col span={8}>
          <Field label="Sales Person *">
            <Select
              value={values.organizerEmail || undefined}
              options={organizerOptions}
              onChange={organizerEmail => update({ organizerEmail }, ['description', 'emailBody'])}
              style={{ width: '100%' }}
              status={values.organizerEmail ? '' : 'error'}
            />
          </Field>
        </Col>
        <Col span={8}>
          <Field label={<span>Associated Project {values.projectId && values.projectSold ? <Text type="warning">SOLD</Text> : null}</span>}>
            <Select
              allowClear
              value={values.projectId ?? undefined}
              options={projectOptions}
              onChange={projectId => update({ projectId: projectId ?? null }, ['description', 'summary'])}
              style={{ width: '100%' }}
            />
          </Field>
        </Col>
        <Col span={8}>
          <Field label="Time frame for project">
            <Input value={values.timeFrame} onChange={e => update({ timeFrame: e.target.value })} />
          </Field>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Field label="Best time to call">
            <Input value={values.bestTimeToCall} onChange={e => update({ bestTimeToCall: e.target.value })} />
          </Field>
        </Col>
        <Col span={8}>
          <Field label="Consultation Date *">
            <DatePicker
              showTime
              use12Hours
              format={DATE_FORMAT}
              value={values.consultationDate}
              onChange={(date: Dayjs | null) => update({ consultationDate: date }, ['description', 'emailBody', 'summary'])}
              style={{ width: '100%' }}
              status={values.consultationDate ? '' : 'error'}
            />
          </Field>
        </Col>
        <Col span={8}>
          <Field label="Consultation Fee">
            <Input value={values.fee} onChange={e => update({ fee: e.target.value })} />
          </Field>
        </Col>
      </Row>

      <div>
        <Text strong>Sales Leads *</Text>
        <Space wrap style={{ display: 'flex', marginTop: 8 }}>
          {salesLeads.map(lead => <Space key={lead} size={4}>
            <Switch
              size="small"
              checked={values.selectedSalesLeads[lead] === true}
              onChange={checked => update({ selectedSalesLeads: { ...values.selectedSalesLeads, [lead]: checked } }, ['description'])}
            />
            <Text>{lead}</Text>
          </Space>)}
        </Space>
        {!salesLeadSelected && <div><Text type="danger"><small>You must select at least one Sales Lead.</small></Text></div>}
      </div>

      {cannedResponses.length > 0 && <Field label="Canned Responses">
        <Select
          value={values.cannedResponse}
          options={cannedResponseOptions}
          onChange={cannedResponse => update({ cannedResponse }, ['description', 'emailBody', 'summary'])}
          style={{ width: '100%' }}
        />
      </Field>}

      <div>
        <Button type="link" size="small" icon={<FontAwesomeIcon icon={faMagic} />} onClick={() => update({}, ['description', 'emailBody', 'summary'])}>
          Autofill details
        </Button>
      </div>

      <Row gutter={16}>
        <Col span={16}>
          <Field label={<span>Summary <small>also used as the subject of the invitation *</small></span>}>
            <Input value={values.summary} onChange={e => update({ summary: e.target.value })} status={values.summary ? '' : 'error'} />
          </Field>
        </Col>
        <Col span={8}>
          <Field label="Status">
            <Select value={values.status} options={statusOptions} onChange={status => update({ status })} style={{ width: '100%' }} />
          </Field>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={16}>
          <Field label={<span>Event Description <small>body of the calendar event</small></span>}>
            <Input.TextArea rows={8} value={values.description} onChange={e => update({ description: e.target.value })} />
          </Field>
        </Col>
        <Col span={8}>
          <Field label="Internal Notes">
            <Input.TextArea rows={8} value={values.notes} onChange={e => update({ notes: e.target.value })} />
          </Field>
        </Col>
      </Row>

      <Field label={<span>Email Description <small>body of the email invitation</small></span>}>
        <RichTextEditor value={values.emailBody} onChange={emailBody => update({ emailBody })} />
      </Field>
    </VSpace>
  </Modal>
}

type FieldProps = {
  label: React.ReactNode
  children: React.ReactNode
}

const Field = ({ label, children }: FieldProps) => (
  <Space direction="vertical" size={2} style={{ width: '100%' }}>
    <Text type="secondary"><small>{label}</small></Text>
    {children}
  </Space>
)

export default ConsultationModal
